import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogDescription,
} from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { ConfirmDialog } from '../../../ui/confirm-dialog';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Label } from '../../../ui/label';
import { Checkbox } from '../../../ui/checkbox';
import { Spinner } from '../../../ui/spinner';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '../../../ui/select';
import { useTheme } from '../../../../contexts/theme/ThemeContext';
import {
 IconSpeakerphone,
 IconBuildingStore,
 IconCurrencyDollar,
 IconCalendarDue,
 IconUsers,
 IconCheck,
 IconRocket,
 IconBrandDiscord,
 IconWorld,
 IconChevronDown,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';
import campaignService from '../../../../services/campaignService';
import brandService from '../../../../services/brandService';
import {
 Sheet,
 SheetContent,
 SheetHeader,
 SheetTitle,
 SheetDescription,
} from '../../../ui/sheet';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { cn } from '../../../../lib/utils';
import { useDirtyNavigationGuard } from '../../../routing/DirtyNavigationGuard';

const DEFAULT_FORM_DATA = {
 name: '',
 brandId: '',
 campaignType: 'REGULAR',
 creatorType: 'web3',
 pricingType: 'DEFAULT',
 hasBrief: true,
 requiresDrafts: false,
 twitterSource: '',
 briefingDeadline: '',
 deliveryDeadline: '',
 deadline: '',
 requiredCreators: '',
 priority: 'MEDIUM',
 announceOnCreate: false,
 announcementText: '',
};

const CAMPAIGN_TYPES = [
 { type: 'REGULAR', icon: IconSpeakerphone, label: 'Regular', desc: 'Brief, review, publish, and report through the standard workflow.' },
 { type: 'COMMENT_BASED', icon: IconBrandDiscord, label: 'Comment-Based', desc: 'Track creator replies on one source X post.' },
 { type: 'AMPLIFICATION', icon: IconRocket, label: 'Amplification', desc: 'Run the existing automated quote-post and comment series.' },
 { type: 'EXTERNAL', icon: IconWorld, label: 'External', desc: 'Manage creators manually without Discord onboarding.' },
];

export const toLocalDateInput = (date) => {
 const year = date.getFullYear();
 const month = String(date.getMonth() + 1).padStart(2, '0');
 const day = String(date.getDate()).padStart(2, '0');
 return `${year}-${month}-${day}`;
};

const endOfLocalDate = (value) => {
 const [year, month, day] = value.split('-').map(Number);
 return new Date(year, month - 1, day, 23, 59, 59, 999);
};

export const normalizeTwitterStatusUrl = (value) => {
 const trimmed = value?.trim();
 if (!trimmed) return '';

 try {
 const parsed = new URL(trimmed);
 const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
 const isAllowedHost = hostname === 'x.com' || hostname === 'twitter.com';
 const isStatusPath = /^\/(?:i\/web|[a-z0-9_]{1,15})\/status\/\d+\/?$/i.test(parsed.pathname);
 if (
 parsed.protocol !== 'https:' ||
 parsed.username ||
 parsed.password ||
 parsed.port ||
 parsed.hash ||
 !isAllowedHost ||
 !isStatusPath
 ) {
 throw new Error('Source X post must be an HTTPS x.com or twitter.com status URL');
 }
 return parsed.href;
 } catch (error) {
 if (error.message === 'Source X post must be an HTTPS x.com or twitter.com status URL') throw error;
 throw new Error('Source X post must be an HTTPS x.com or twitter.com status URL');
 }
};

export const buildCampaignPayload = (formData, now = new Date()) => {
 const hasBrief = formData.hasBrief === true;
 const isCommentBased = formData.campaignType === 'COMMENT_BASED';
 const isCommentBasedNoBrief = isCommentBased && !hasBrief;
 const isAmplification = formData.campaignType === 'AMPLIFICATION';
 const isExternal = formData.campaignType === 'EXTERNAL';
 const twitterSource = isCommentBased ? normalizeTwitterStatusUrl(formData.twitterSource) : '';
 const announcementText = formData.announcementText?.trim();
 if (isAmplification && formData.announceOnCreate === true && !announcementText) {
 throw new Error('Enter an announcement message or turn off announcement on creation');
 }

 return {
 name: formData.name.trim(),
 brandId: formData.brandId,
 campaignType: formData.campaignType,
 creatorType: [formData.creatorType || 'web3'],
 pricingType: formData.pricingType,
 hasBrief: isCommentBased ? hasBrief : true,
 requiresDrafts: formData.campaignType === 'REGULAR' && formData.requiresDrafts === true,
 startDate: now.toISOString(),
 endDate: endOfLocalDate(formData.deadline).toISOString(),
 briefingDeadline:
 !isCommentBasedNoBrief && formData.briefingDeadline && !isAmplification && !isExternal
 ? new Date(formData.briefingDeadline).toISOString()
 : null,
 deliveryDeadline:
 !isCommentBasedNoBrief && formData.deliveryDeadline && !isAmplification && !isExternal
 ? new Date(formData.deliveryDeadline).toISOString()
 : null,
 requiredCreators: isCommentBasedNoBrief ? 0 : Math.max(1, parseInt(formData.requiredCreators, 10) || 1),
 priority: formData.priority || 'MEDIUM',
 ...(isCommentBased && twitterSource && { twitterSource }),
 ...(isAmplification && formData.announceOnCreate === true && announcementText && {
 announceOnCreate: true,
 announcementText,
 }),
 };
};

export const validateCampaignForm = (formData, now = new Date()) => {
 if (!formData.name?.trim() || !formData.brandId?.trim() || !formData.deadline?.trim()) {
 return 'Enter a campaign name, choose a brand, and select an end date.';
 }
 if (endOfLocalDate(formData.deadline) < now) {
 return 'End date must be today or later.';
 }

 const usesWorkflowDeadlines = !(
 (formData.campaignType === 'COMMENT_BASED' && formData.hasBrief !== true)
 || formData.campaignType === 'AMPLIFICATION'
 || formData.campaignType === 'EXTERNAL'
 );
 if (!usesWorkflowDeadlines) return '';
 if (formData.briefingDeadline && formData.briefingDeadline > formData.deadline) {
 return 'Briefing deadline must be on or before the campaign end date.';
 }
 if (formData.deliveryDeadline && formData.deliveryDeadline > formData.deadline) {
 return 'Delivery deadline must be on or before the campaign end date.';
 }
 if (formData.briefingDeadline && formData.deliveryDeadline
 && formData.briefingDeadline > formData.deliveryDeadline) {
 return 'Briefing deadline must be on or before the delivery deadline.';
 }
 return '';
};

const CreateCampaignModal = ({ open, onClose, onSuccess, referenceDate }) => {
 const isMobile = useMediaQuery('(max-width: 639px)');
 const { isDarkMode: isDark } = useTheme();

 const [loading, setLoading] = useState(false);
 const [brands, setBrands] = useState([]);
 const [brandsLoading, setBrandsLoading] = useState(false);
 const [brandsError, setBrandsError] = useState('');
 const [submitError, setSubmitError] = useState('');
 const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
 const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
 const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
 const submittingRef = useRef(false);
 const isDirty = Object.keys(DEFAULT_FORM_DATA).some((key) => formData[key] !== DEFAULT_FORM_DATA[key]);
 const resetCampaignDraft = useCallback(() => {
 setFormData(DEFAULT_FORM_DATA);
 setMoreOptionsOpen(false);
 setSubmitError('');
 setConfirmDiscardOpen(false);
 }, []);
 const {
 setDirty: setCampaignDirty,
 setInFlight: setCampaignInFlight,
 release: releaseCampaignGuard,
 } = useDirtyNavigationGuard('create-campaign', { onDiscard: resetCampaignDraft });

 useEffect(() => {
 setCampaignDirty(open && isDirty);
 }, [isDirty, open, setCampaignDirty]);

 const handleRequestClose = () => {
 if (submittingRef.current || loading) return;
 if (isDirty) {
 setConfirmDiscardOpen(true);
 return;
 }
 releaseCampaignGuard();
 onClose();
 };

 const discardConfirmation = (
 <ConfirmDialog
 open={open && confirmDiscardOpen}
 onOpenChange={setConfirmDiscardOpen}
 title="Discard unsaved campaign?"
 description="This campaign has not been created. Discarding will remove the details you entered."
 cancelText="Keep editing"
 confirmText="Discard campaign"
 variant="destructive"
 onEscapeKeyDown={(event) => event.stopPropagation()}
 onConfirm={() => {
 if (submittingRef.current || loading) return;
 setConfirmDiscardOpen(false);
 releaseCampaignGuard();
 onClose();
 }}
 />
 );

 // Fetch brands when modal opens
 useEffect(() => {
 if (open) {
 setMoreOptionsOpen(false);
 fetchBrands();
 } else {
 resetCampaignDraft();
 }
 }, [open, resetCampaignDraft]);

 const fetchBrands = async () => {
 setBrandsLoading(true);
 setBrandsError('');
 try {
 const response = await brandService.getAllBrands();
 setBrands(Array.isArray(response) ? response : response.data || []);
 } catch (error) {
 console.error('Error fetching brands:', error);
 setBrandsError('Brands could not be loaded. Retry before creating the campaign.');
 toast.error('Failed to load brands');
 } finally {
 setBrandsLoading(false);
 }
 };

 const AMPLIFICATION_DEFAULT_ANNOUNCEMENT = 'New amplification campaign is live! React with \u2705 to join.';

 const handleChange = (field, value) => {
 setFormData((prev) => ({ ...prev, [field]: value }));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();

 if (submittingRef.current) return;

 const now = referenceDate || new Date();
 const validationError = validateCampaignForm(formData, now);
 if (validationError) {
 setSubmitError(validationError);
 toast.error(validationError);
 return;
 }

 submittingRef.current = true;
 setCampaignInFlight(true);
 setLoading(true);
 setSubmitError('');
 try {
 const campaignData = buildCampaignPayload(formData, now);

 const response = await campaignService.createCampaign(campaignData);

 if (!response) {
 throw new Error('No response received from server');
 }

 toast.success('Campaign created successfully');

 releaseCampaignGuard();
 resetCampaignDraft();

 if (onSuccess) {
 onSuccess(response);
 }

 onClose();
 } catch (error) {
 console.error('Error creating campaign:', error);
 const message = error.message || 'Failed to create campaign';
 setSubmitError(`${message}. Your campaign details are still here; review them and try again.`);
 toast.error(message);
 } finally {
 submittingRef.current = false;
 setLoading(false);
 setCampaignInFlight(false);
 }
 };

 const handleCampaignTypeChange = (type) => {
 setFormData((prev) => ({
 ...prev,
 campaignType: type,
 pricingType: (type === 'AMPLIFICATION' || type === 'EXTERNAL') ? 'QRT' : prev.pricingType,
 announceOnCreate: type === 'AMPLIFICATION' ? prev.announceOnCreate : false,
 }));
 };

 const handleAnnouncementOptIn = (checked) => {
 setFormData((prev) => ({
 ...prev,
 announceOnCreate: checked === true,
 announcementText:
 checked === true && !prev.announcementText
 ? AMPLIFICATION_DEFAULT_ANNOUNCEMENT
 : prev.announcementText,
 }));
 };

 const isCommentBasedNoBrief = formData.campaignType === 'COMMENT_BASED' && formData.hasBrief !== true;
 const selectedCampaignType = CAMPAIGN_TYPES.find(({ type }) => type === formData.campaignType);

 // Selectable card helper
 const SelectableCard = ({ selected, onClick, children, className, ariaLabel }) => (
 <button
 type="button"
 onClick={onClick}
 aria-pressed={selected}
 aria-label={ariaLabel}
 className={cn(
 'w-full p-4 text-left cursor-pointer border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
 selected
 ? 'border-accent bg-accent/5 hover:bg-accent/10'
 : 'border-glass-border hover:border-glass-border-hover',
 !selected && (isDark ? 'bg-white/[0.02] hover:bg-white/[0.04]' : 'bg-black/[0.01] hover:bg-black/[0.02]'),
 className
 )}
 >
 {children}
 </button>
 );

 const formContent = (
 <form onSubmit={handleSubmit} noValidate>
 <div className="flex flex-col gap-4 py-4">
 {submitError && (
 <div role="alert" className="border border-error/30 bg-error-muted/20 p-3 text-sm text-error">
 {submitError}
 </div>
 )}
 <section className="border border-glass-border bg-surface-1" aria-labelledby="campaign-basics-title">
 <div className="border-b border-glass-border px-4 py-3">
 <h3 id="campaign-basics-title" className="text-base font-semibold text-text-primary">Campaign basics</h3>
 <p className="mt-1 text-sm text-text-secondary">Only these four details are needed to get started.</p>
 </div>
 <div className="flex flex-col gap-4 p-4">
 {/* Campaign Name & Brand Selection - Same Row */}
 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
 {/* Campaign Name */}
 <div className="sm:col-span-2">
 <Label htmlFor="campaign-name" className="mb-2 block">Campaign Name *</Label>
 <div className="relative">
 <IconSpeakerphone
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="campaign-name"
 required
 value={formData.name}
 onChange={(e) => handleChange('name', e.target.value)}
 className="pl-10"
 placeholder="Enter campaign name"
 />
 </div>
 </div>

 {/* Brand Selection */}
 <div>
 <Label htmlFor="brand-select" className="mb-2 block">Brand *</Label>
 <Select
 value={formData.brandId}
 onValueChange={(value) => handleChange('brandId', value)}
 disabled={brandsLoading || Boolean(brandsError)}
 >
 <SelectTrigger id="brand-select" aria-label="Brand">
 <div className="flex items-center gap-2">
 <IconBuildingStore size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue placeholder={brandsLoading ? 'Loading brands...' : 'Select a brand'} />
 </div>
 </SelectTrigger>
 <SelectContent>
 {brands.map((brand) => (
 <SelectItem key={brand._id} value={brand._id}>
 {brand.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 {brandsError && (
 <div className="mt-2 flex flex-col gap-2 border border-error/30 bg-error-muted/20 p-3" role="alert">
 <p className="text-sm text-error">{brandsError}</p>
 <button
 type="button"
 onClick={fetchBrands}
 className="min-h-[44px] self-start border border-glass-border px-3 py-2 text-sm font-medium text-text-primary hover:bg-glass-bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
 >
 Retry loading brands
 </button>
 </div>
 )}
 </div>
 </div>

 {/* Campaign Type Selection */}
 <div>
 <Label className="mb-2 block text-text-secondary">Campaign type</Label>
 <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="group" aria-label="Campaign type">
 {CAMPAIGN_TYPES.map(({ type, icon: Icon, label }) => (
 <button
 type="button"
 key={type}
 onClick={() => handleCampaignTypeChange(type)}
 aria-pressed={formData.campaignType === type}
 aria-label={`${label} campaign`}
 className={cn(
 'flex min-h-[44px] items-center gap-2 border px-3 py-2 text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
 formData.campaignType === type
 ? 'border-accent bg-accent/10 text-text-primary'
 : 'border-glass-border bg-glass-bg text-text-secondary hover:border-glass-border-hover hover:text-text-primary'
 )}
 >
 <Icon size={16} className={formData.campaignType === type ? 'text-accent' : 'text-text-tertiary'} />
 <span className="min-w-0 flex-1 truncate">{label}</span>
 {formData.campaignType === type && (
 <IconCheck size={14} className="shrink-0 text-accent" />
 )}
 </button>
 ))}
 </div>
 <p className="mt-2 text-sm text-text-secondary">{selectedCampaignType?.desc}</p>
 </div>

 {/* Final Deadline */}
 <div>
 <Label htmlFor="final-deadline" className="mb-2 block">End date *</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="final-deadline"
 type="date"
 required
 min={toLocalDateInput(referenceDate || new Date())}
 value={formData.deadline}
 onChange={(e) => handleChange('deadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 </div>
 </section>

 <Button
 type="button"
 variant="outline"
 aria-expanded={moreOptionsOpen}
 aria-controls="campaign-more-options"
 onClick={() => setMoreOptionsOpen((current) => !current)}
 className="justify-between"
 >
 More options
 <IconChevronDown
 size={18}
 className={cn('transition-transform', moreOptionsOpen && 'rotate-180')}
 />
 </Button>

 {moreOptionsOpen && (
 <div id="campaign-more-options" className="ui-motion-enter flex flex-col gap-4 border border-glass-border p-4">

 {formData.campaignType === 'AMPLIFICATION' && (
 <div className="border border-warning/40 bg-warning-muted/30 p-4">
 <div className="flex items-start gap-3">
 <Checkbox
 id="announce-on-create"
 checked={formData.announceOnCreate}
 onCheckedChange={handleAnnouncementOptIn}
 className="mt-0.5"
 />
 <div>
 <Label htmlFor="announce-on-create" className="cursor-pointer">
 Announce to creators when this campaign is created
 </Label>
 <p className="text-xs text-text-secondary mt-0.5">
 This immediately sends the message through the configured Discord and Telegram channels.
 </p>
 </div>
 </div>
 {formData.announceOnCreate && (
 <div className="relative mt-3">
 <Label htmlFor="campaign-announcement" className="mb-2 block text-text-secondary">
 Announcement message
 </Label>
 <IconBrandDiscord
 size={18}
 className="absolute left-3 top-10 text-text-tertiary"
 />
 <Textarea
 id="campaign-announcement"
 required
 rows={4}
 placeholder="Enter the announcement message for creators..."
 value={formData.announcementText}
 onChange={(e) => handleChange('announcementText', e.target.value)}
 className="pl-10"
 />
 </div>
 )}
 </div>
 )}

 {/* Creator Audience */}
 <div>
 <Label htmlFor="creator-audience" className="mb-2 block">Creator Audience</Label>
 <Select
 value={formData.creatorType}
 onValueChange={(value) => handleChange('creatorType', value)}
 >
 <SelectTrigger id="creator-audience">
 <div className="flex items-center gap-2">
 <IconUsers size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue />
 </div>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="web3">Web3 Creators</SelectItem>
 <SelectItem value="ai">AI Creators</SelectItem>
 </SelectContent>
 </Select>
 </div>

 {/* Pricing Type - Hide for AMPLIFICATION and EXTERNAL */}
 {formData.campaignType !== 'AMPLIFICATION' && formData.campaignType !== 'EXTERNAL' && (
 <div>
 <Label htmlFor="pricing-type" className="mb-2 block">Pricing Type</Label>
 <Select
 value={formData.pricingType}
 onValueChange={(value) => handleChange('pricingType', value)}
 >
 <SelectTrigger id="pricing-type">
 <div className="flex items-center gap-2">
 <IconCurrencyDollar size={18} className="text-text-tertiary flex-shrink-0" />
 <SelectValue />
 </div>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="DEFAULT">Default Pricing</SelectItem>
 <SelectItem value="QRT">QRT Pricing</SelectItem>
 <SelectItem value="SHORT_FORM">Short Form Pricing</SelectItem>
 </SelectContent>
 </Select>
 </div>
 )}

 {/* Has Brief - Only for COMMENT_BASED */}
 {formData.campaignType === 'COMMENT_BASED' && (
 <div>
 <Label className="mb-3 block text-text-secondary">Include Briefing Stage?</Label>
 <div className="grid grid-cols-2 gap-3" role="group" aria-label="Include briefing stage">
 {[
 { value: true, label: 'Yes' },
 { value: false, label: 'No' },
 ].map(({ value, label }) => (
 <SelectableCard
 key={label}
 selected={formData.hasBrief === value}
 onClick={() => handleChange('hasBrief', value)}
 ariaLabel={`${label}, ${label === 'Yes' ? 'include' : 'skip'} briefing stage`}
 >
 <div className="flex items-center justify-center">
 <span className="text-sm font-medium text-text-primary">{label}</span>
 {formData.hasBrief === value && (
 <IconCheck size={14} className="text-accent ml-2" />
 )}
 </div>
 </SelectableCard>
 ))}
 </div>
 </div>
 )}

 {formData.campaignType === 'COMMENT_BASED' && (
 <div>
 <Label htmlFor="twitter-source" className="mb-2 block">Source X post</Label>
 <Input
 id="twitter-source"
 type="url"
 value={formData.twitterSource}
 onChange={(e) => handleChange('twitterSource', e.target.value)}
 placeholder="https://x.com/..."
 />
 </div>
 )}

 {/* Require Drafts - Only for REGULAR with hasBrief */}
 {formData.campaignType === 'REGULAR' && formData.hasBrief && (
 <div className="flex items-start gap-3">
 <Checkbox
 id="require-drafts"
 checked={formData.requiresDrafts}
 onCheckedChange={(checked) => handleChange('requiresDrafts', checked)}
 className="mt-0.5"
 />
 <div>
 <Label htmlFor="require-drafts" className="cursor-pointer">Require Draft Reviews?</Label>
 <p className="text-xs text-text-secondary mt-0.5">
 Creators must submit drafts for review before posting
 </p>
 </div>
 </div>
 )}

 {/* Deadlines - Hide for AMPLIFICATION and EXTERNAL */}
 {!isCommentBasedNoBrief && formData.campaignType !== 'AMPLIFICATION' && formData.campaignType !== 'EXTERNAL' && (
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div>
 <Label htmlFor="briefing-deadline" className="mb-2 block">Briefing Deadline</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="briefing-deadline"
 type="date"
 value={formData.briefingDeadline}
 onChange={(e) => handleChange('briefingDeadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 <div>
 <Label htmlFor="delivery-deadline" className="mb-2 block">Delivery Deadline</Label>
 <div className="relative">
 <IconCalendarDue
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="delivery-deadline"
 type="date"
 value={formData.deliveryDeadline}
 onChange={(e) => handleChange('deliveryDeadline', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 </div>
 )}

 {/* Creator target */}
 {!isCommentBasedNoBrief && (
 <div>
 <Label htmlFor="required-creators" className="mb-2 block">Required Creators</Label>
 <div className="relative">
 <IconUsers
 size={18}
 className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
 />
 <Input
 id="required-creators"
 type="number"
 min="1"
 value={formData.requiredCreators}
 onChange={(e) => handleChange('requiredCreators', e.target.value)}
 className="pl-10"
 />
 </div>
 </div>
 )}

 {/* Priority */}
 <div>
 <Label htmlFor="campaign-priority" className="mb-2 block">Priority</Label>
 <Select
 value={formData.priority}
 onValueChange={(value) => handleChange('priority', value)}
 >
 <SelectTrigger id="campaign-priority">
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="LOW">Low</SelectItem>
 <SelectItem value="MEDIUM">Medium</SelectItem>
 <SelectItem value="HIGH">High</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 )}
 </div>

 <div className="sticky bottom-0 z-10 -mx-1 flex flex-col-reverse gap-2 border border-glass-border bg-surface-1/95 p-3 shadow-lg backdrop-blur sm:flex-row sm:justify-end">
 <Button
 type="button"
 variant="ghost"
 onClick={handleRequestClose}
 disabled={loading}
 >
 Cancel
 </Button>
 <Button
 type="submit"
 disabled={loading}
 >
 {loading && <Spinner size="sm" className="mr-2" />}
 {loading ? 'Creating...' : 'Create Campaign'}
 </Button>
 </div>
 </form>
 );

 if (isMobile) {
 return (
 <>
 <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) handleRequestClose(); }}>
 <SheetContent side="bottom" className="h-[95vh] overflow-y-auto p-4" showCloseButton={!loading}>
 <SheetHeader className="mb-2">
 <SheetTitle>Create New Campaign</SheetTitle>
 <SheetDescription className="sr-only">Configure and create a campaign.</SheetDescription>
 </SheetHeader>
 {formContent}
 </SheetContent>
 </Sheet>
 {discardConfirmation}
 </>
 );
 }

 return (
 <>
 <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleRequestClose(); }}>
 <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" showCloseButton={!loading}>
 <DialogHeader>
 <DialogTitle>Create New Campaign</DialogTitle>
 <DialogDescription className="sr-only">Configure and create a campaign.</DialogDescription>
 </DialogHeader>
 {formContent}
 </DialogContent>
 </Dialog>
 {discardConfirmation}
 </>
 );
};

export default CreateCampaignModal;
